import { pluralize } from '../pluralize';

/**
 * Подмножество ISO 8601 calendar durations: только PnD, PnW, PnM (без времени и комбинаций).
 */
export type Iso8601CalendarDurationUnit = 'D' | 'W' | 'M';
export type Iso8601CalendarDuration = `${'P'}${number}${Iso8601CalendarDurationUnit}`;

const VALID_UNITS = new Set(['D', 'W', 'M']);

function isUnit(char: string): char is Iso8601CalendarDurationUnit {
    return VALID_UNITS.has(char);
}

export function parseIso8601DurationToParts(
    duration: string | undefined
): { unit: Iso8601CalendarDurationUnit; count: number } | null {
    if (!duration || duration[0] !== 'P' || duration.length < 3) {
        return null;
    }

    const lastChar = duration.at(-1)?.toUpperCase();

    if (!lastChar || !isUnit(lastChar)) {
        return null;
    }

    const numberPart = duration.slice(1, -1);
    const count = Number.parseInt(numberPart, 10);

    if (Number.isNaN(count) || !Number.isFinite(count) || count <= 0 || String(count) !== numberPart) {
        return null;
    }

    return { unit: lastChar, count };
}

export function buildIso8601Duration(unit: Iso8601CalendarDurationUnit, count: number): Iso8601CalendarDuration | null {
    if (!Number.isFinite(count) || !Number.isInteger(count) || count <= 0) {
        return null;
    }

    return `P${count}${unit}`;
}

export function formatDateDuration(period: string | undefined): string | null {
    const parts = parseIso8601DurationToParts(period);

    if (!parts) {
        return null;
    }

    const { unit, count } = parts;

    if (unit === 'D') {
        return pluralize(count, '%d день', '%d дня', '%d дней');
    }

    if (unit === 'W') {
        return pluralize(count, '%d неделя', '%d недели', '%d недель');
    }

    return pluralize(count, '%d месяц', '%d месяца', '%d месяцев');
}
